import { icon } from "../lib/icon.js";

/**
 * The mic — one component, one state machine (UX spec v3 §4). A port of `setMic`
 * in `docs/design/v3/tools/shared.js`: the state lives in `data-state`, every
 * state carries its own ARIA, and a composer around the mic follows it.
 *
 * Press and hover are CSS (`:active`, `@media (hover:hover)`); the two states
 * stay in the machine because the design names them and the mock-ups draw them.
 * The level bars, the timer and the placeholder belong to the composer
 * (`ui/composer.ts`), which listens to the product's own voice input rather than
 * to a demo generator.
 */
export const MIC_STATES = ["idle", "hover", "pressed", "listening", "processing", "disabled"] as const;
export type MicState = (typeof MIC_STATES)[number];

const LABEL: Readonly<Record<MicState, string>> = {
  idle: "Speak",
  hover: "Speak",
  pressed: "Speak",
  listening: "Stop listening",
  processing: "Working",
  disabled: "Microphone is off",
};

/**
 * A true circle with the three glyphs the CSS crossfades between. `withSend` (the
 * composer's action slot, DCTA-88 §D.1) adds the Send face: a disc with the arrow
 * that grows over the mic when `data-mode="send"` (vault.css §9.2).
 */
export function micButton(idleLabel?: string, withSend = false): HTMLButtonElement {
  const mic = document.createElement("button");
  mic.type = "button";
  mic.className = "mic";
  if (idleLabel !== undefined) mic.dataset["idleLabel"] = idleLabel;
  mic.append(icon("i-mic", "g-mic"), icon("i-stop", "g-stop"), icon("i-spin", "g-spin"));
  if (withSend) {
    const sd = document.createElement("span");
    sd.className = "sd";
    sd.setAttribute("aria-hidden", "true");
    sd.append(icon("i-arrow-up"));
    mic.append(sd);
    mic.dataset["mode"] = "mic";
  }
  setMic(mic, "idle");
  return mic;
}

export function setMic(mic: HTMLElement, state: MicState): void {
  if (!(MIC_STATES as readonly string[]).includes(state)) return;
  mic.dataset["state"] = state;
  mic.setAttribute("aria-pressed", state === "listening" ? "true" : "false");
  mic.setAttribute("aria-disabled", state === "disabled" ? "true" : "false");
  mic.setAttribute("aria-busy", state === "processing" ? "true" : "false");
  const idleLabel = mic.dataset["idleLabel"];
  const speaking = state === "idle" || state === "hover" || state === "pressed";
  mic.setAttribute("aria-label", speaking && idleLabel !== undefined ? idleLabel : LABEL[state]);
  if (mic.dataset["mode"] === "send") sendFace(mic);

  const composer = mic.closest<HTMLElement>(".composer");
  if (composer !== null) {
    composer.dataset["state"] =
      state === "listening" || state === "processing" || state === "disabled"
        ? state
        : composer.dataset["state"] === "typing"
          ? "typing"
          : "idle";
  }
}

/**
 * One action slot, two faces (DCTA-88 §D.1; a port of `setSend` in shared.js): Send
 * while the field has text, the mic when it is empty. The semantic state — `data-mode`
 * and the label — changes at once, never on `transitionend`, so a type-and-delete
 * faster than the 150 ms morph reverses the CSS transition from wherever it is.
 * The mic's own `data-state` is kept as it is (a disabled microphone stays
 * `disabled` under the Send face), so clearing the field brings back exactly the
 * mic the customer had. `held`: a turn is in flight — Send shows but does not act.
 */
export function setSend(mic: HTMLElement, on: boolean, held = false): void {
  if (held) mic.dataset["held"] = "true";
  else delete mic.dataset["held"];
  if (on) {
    mic.dataset["mode"] = "send";
    sendFace(mic);
    return;
  }
  const was = mic.dataset["mode"];
  mic.dataset["mode"] = "mic";
  if (was === "send") setMic(mic, micState(mic));
}

function sendFace(mic: HTMLElement): void {
  mic.setAttribute("aria-label", "Send");
  mic.removeAttribute("aria-pressed");
  mic.setAttribute("aria-busy", "false");
  mic.setAttribute("aria-disabled", mic.dataset["held"] === "true" ? "true" : "false");
}

export function micState(mic: HTMLElement): MicState {
  const s = mic.dataset["state"];
  return (MIC_STATES as readonly string[]).includes(s ?? "") ? (s as MicState) : "idle";
}

/** A light haptic where the platform has one (Android); iOS web has none. */
export function haptic(): void {
  const nav = globalThis.navigator as Navigator & { vibrate?: (ms: number) => boolean };
  try {
    nav.vibrate?.(10);
  } catch {
    /* a missing motor is not an error */
  }
}
